import { Controller } from "@hotwired/stimulus"
import { CheckoutService } from "../../services/checkout_service"

/**
 * Bag Checkout Controller - Section Navigation
 * 
 * Manages the progressive 3-section accordion checkout flow:
 * 1. Contact Information (email)
 * 2. Shipping & Billing Address
 * 3. Payment Method
 * 
 * Responsibilities:
 * - Section open/close/edit navigation
 * - Form validation coordination
 * - Backend submission via CheckoutService
 * - Button loading states
 * 
 * @see /docs/single_page_checkout.md for full documentation
 */
export default class extends Controller {
    static targets = [
        "section",
        "sectionBody",
        "billToShipCheckbox"
    ]

    connect() {
        //console.log('Bag checkout controller connected');
        
        // Initialize checkout service
        this.checkoutService = new CheckoutService();
        
        this.currentSection = 1;
        
        // Initialize billing form visibility
        this.initializeBillingForm();
        
        // Handle browser back/forward buttons
        this.handlePopState = this.handlePopState.bind(this);
        window.addEventListener('popstate', this.handlePopState);
        
        // Listen for state restoration events
        this.element.addEventListener('bag--checkout-state:restoreComplete', this.handleRestoreComplete.bind(this));
        
        // Scroll to payment error if card was declined
        this.scrollToPaymentError();
    }
    
    disconnect() {
        window.removeEventListener('popstate', this.handlePopState);
        this.element.removeEventListener('bag--checkout-state:restoreComplete', this.handleRestoreComplete.bind(this));
    }
    
    /**
     * Handle state restoration complete event
     */
    handleRestoreComplete(event) {
        const { openSection } = event.detail;
        if (openSection) {
            this.openSection(openSection);
        }
    }
    
    /**
     * Handle browser back/forward navigation
     */
    handlePopState(event) {
        if (event.state && event.state.section) {
            //console.log('Navigating to section:', event.state.section);
            const section = this.sectionTargets[event.state.section - 1];
            if (!section.classList.contains('active')) {
                this.openSection(event.state.section);
            }
        }
    }
    
    /**
     * Initialize billing form visibility based on checkbox state
     */
    initializeBillingForm() {
        if (this.hasBillToShipCheckboxTarget) {
            const billingFormDiv = document.getElementById('billing-address-form');
            if (billingFormDiv) {
                if (this.billToShipCheckboxTarget.checked) {
                    billingFormDiv.style.display = 'none';
                } else {
                    billingFormDiv.style.display = 'block';
                }
            }
        }
    }

    /**
     * Prevent default Enter key behavior (prevents form submission)
     */
    preventDefault(event) {
        event.preventDefault();
    }

    /**
     * Complete contact section and move to shipping
     */
    async completeContact(event) {
        event.preventDefault();
        event.stopPropagation();
        
        const button = event.currentTarget;
        const email = this.element.querySelector('[data-bag--checkout-target="emailInput"]').value;
        
        //console.log('Completing contact section...');
        
        // Validate email field first (no loading state)
        const isValid = this.validateField('order[email]');
        if (!isValid) {
            //console.log('Email validation failed');
            return false;
        }
        
        // Show loading state for backend save
        this.setButtonLoading(button, 'Saving...');
        const result = await this.checkoutService.saveContact(email);
        
        if (!result.success) {
            //console.log('Contact save failed');
            this.resetButton(button);
            alert('Error saving contact information. Please try again.');
            return false;
        }
        
        //console.log('Contact info saved successfully');
        this.resetButton(button);
        
        // Trigger completion event for other controllers
        this.dispatch('sectionCompleted', { 
            detail: { section: 1, email } 
        });
        
        // Open next section
        this.openSection(2);
        
        return false;
    }

    /**
     * Complete shipping section and move to payment
     */
    async completeShipping(event) {
        event.preventDefault();
        event.stopPropagation();
        
        const button = event.currentTarget;
        
        //console.log('Completing shipping section...');
        
        // Validate shipping/billing fields first (no loading state)
        const isValid = this.validateShippingFields();
        if (!isValid) {
            //console.log('Shipping validation failed');
            return false;
        }
        
        // Collect shipping data and show loading state
        this.setButtonLoading(button, 'Saving...');
        const shippingData = this.collectShippingData();
        
        // Save to backend
        const result = await this.checkoutService.saveShipping(shippingData);
        
        if (!result.success) {
            //console.log('Shipping save failed');
            this.resetButton(button);
            alert('Error saving shipping information. Please try again.');
            return false;
        }
        
        //console.log('Shipping data saved successfully');
        
        // Refresh order summary
        await this.checkoutService.refreshOrderSummary();
        
        this.resetButton(button);
        
        // Trigger completion event for other controllers
        this.dispatch('sectionCompleted', { 
            detail: { section: 2 } 
        });
        
        // Open payment section
        this.openSection(3);
        
        return false;
    }
    
    /**
     * Validate a single field using native validation
     */
    validateField(fieldName) {
        const form = this.element.querySelector('form');
        const validator = form._nativeValidator;
        
        if (validator) {
            return validator.validateField(fieldName);
        }
        
        // Fallback: basic validation
        const field = form.querySelector(`[name="${fieldName}"]`);
        if (!field) return true;
        
        const isValid = field.checkValidity();
        if (!isValid) {
            field.classList.add('is-invalid');
            return false;
        }
        field.classList.remove('is-invalid');
        return true;
    }
    
    /**
     * Validate shipping and billing fields
     */
    validateShippingFields() {
        const form = this.element.querySelector('form');
        const validator = form._nativeValidator;
        
        if (!validator) {
            return this.fallbackValidateShipping();
        }
        
        // Build list of fields to validate
        const fieldsToValidate = [
            'order[ship_firstname]', 'order[ship_lastname]', 'order[ship_address1]',
            'order[ship_city]', 'order[ship_state]', 'order[ship_zipcode]', 'order[ship_dayphone]'
        ];
        
        // Add billing fields if billing form is visible
        const billingFormDiv = document.getElementById('billing-address-form');
        const isBillingVisible = billingFormDiv && window.getComputedStyle(billingFormDiv).display !== 'none';
        
        if (isBillingVisible) {
            fieldsToValidate.push(
                'order[bill_firstname]', 'order[bill_lastname]', 'order[bill_address1]',
                'order[bill_city]', 'order[bill_state]', 'order[bill_zipcode]', 'order[bill_dayphone]'
            );
        }
        
        // Validate each field
        let allValid = true;
        for (const fieldName of fieldsToValidate) {
            const isValid = validator.validateField(fieldName);
            if (!isValid) {
                allValid = false;
            }
        }
        
        return allValid;
    }
    
    /**
     * Fallback validation without form validation library
     */
    fallbackValidateShipping() {
        const section = this.sectionTargets[1];
        const requiredFields = section.querySelectorAll('[required]');
        let allValid = true;
        
        const billingFormDiv = document.getElementById('billing-address-form');
        const isBillingHidden = billingFormDiv && window.getComputedStyle(billingFormDiv).display === 'none';
        
        requiredFields.forEach(field => {
            // Skip billing fields if hidden
            if (isBillingHidden && field.name && field.name.includes('bill_')) {
                return;
            }
            
            if (!field.value || field.value.trim() === '') {
                field.classList.add('is-invalid');
                allValid = false;
            } else {
                field.classList.remove('is-invalid');
            }
        });
        
        return allValid;
    }
    
    /**
     * Collect shipping and billing data from form
     */
    collectShippingData() {
        const formElement = this.element.querySelector('form');
        const formData = new FormData(formElement);
        const shippingData = {};
        
        const shippingBillingFields = [
            'ship_firstname', 'ship_lastname', 'ship_address1', 'ship_address2',
            'ship_city', 'ship_state', 'ship_zipcode', 'ship_country', 'ship_dayphone',
            'bill_firstname', 'bill_lastname', 'bill_address1', 'bill_address2',
            'bill_city', 'bill_state', 'bill_zipcode', 'bill_country', 'bill_dayphone',
            'bill_to_ship', 'can_text_phone',
            'save_ship_to_account', 'save_bill_to_account'
        ];
        
        formData.forEach((value, key) => {
            const match = key.match(/order\[(.+)\]/);
            if (match && shippingBillingFields.includes(match[1])) {
                shippingData[match[1]] = value;
            }
            // Also collect non-namespaced checkboxes for saving addresses
            if (key === 'save_ship_to_account' || key === 'save_bill_to_account') {
                shippingData[key] = value;
            }
        });
        
        return shippingData;
    }

    /**
     * Open a section
     */
    openSection(sectionNum) {
        const section = this.sectionTargets[sectionNum - 1];
        const sectionBody = section.querySelector('[data-bag--checkout-target="sectionBody"]');
        const sectionHeader = section.querySelector('[data-bag--checkout-target="sectionHeader"]');
        
        // Add active class to the section
        section.classList.add('active');
        
        // Show body
        sectionBody.style.display = 'block';
        
        // Update ARIA attributes
        if (sectionHeader) {
            sectionHeader.setAttribute('aria-expanded', 'true');
        }
        
        // Update URL hash without page reload
        const sectionNames = ['contact', 'shipping', 'payment'];
        const sectionName = sectionNames[sectionNum - 1];
        if (sectionName) {
            history.pushState({ section: sectionNum }, '', `/checkout#${sectionName}`);
        }
        
        // Scroll to section
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
        // Focus on first field when shipping section opens
        if (sectionNum === 2) {
            setTimeout(() => {
                const firstNameField = this.element.querySelector('input[name="order[ship_firstname]"]');
                if (firstNameField) {
                    firstNameField.focus();
                }
            }, 300);
        }
    }

    /**
     * Edit a completed section
     */
    editSection(event) {
        const sectionNumber = parseInt(event.currentTarget.dataset.sectionNumber);
        const section = this.sectionTargets[sectionNumber - 1];
        
        // Only allow editing completed sections
        if (!section.classList.contains('completed')) {
            return;
        }
        
        const sectionBody = section.querySelector('[data-bag--checkout-target="sectionBody"]');
        const editLink = section.querySelector(`[data-bag--checkout-target="editLink${sectionNumber}"]`);
        
        // Close all other sections first
        this.closeAllSections();
        
        // Uncomplete all sections after the one being edited
        this.sectionTargets.forEach((laterSection, index) => {
            if (index >= sectionNumber) {
                laterSection.classList.remove('completed');
                
                const laterEditLink = laterSection.querySelector(`[data-bag--checkout-target="editLink${index + 1}"]`);
                if (laterEditLink) {
                    laterEditLink.style.display = 'none';
                }
            }
        });
        
        // Dispatch event for summary controller to hide summaries
        this.dispatch('sectionEditing', { 
            detail: { section: sectionNumber } 
        });
        
        // Open this section for editing
        section.classList.add('active');
        section.classList.remove('completed');
        sectionBody.style.display = 'block';
        
        // Update ARIA attributes
        const sectionHeader = section.querySelector('[data-bag--checkout-target="sectionHeader"]');
        if (sectionHeader) {
            sectionHeader.setAttribute('aria-expanded', 'true');
        }
        
        if (editLink) editLink.style.display = 'none';
        
        // Scroll to section
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    /**
     * Close all sections
     */
    closeAllSections() {
        this.sectionTargets.forEach((section, index) => {
            const sectionBody = section.querySelector('[data-bag--checkout-target="sectionBody"]');
            const sectionHeader = section.querySelector('[data-bag--checkout-target="sectionHeader"]');
            const editLink = section.querySelector(`[data-bag--checkout-target="editLink${index + 1}"]`);
            
            section.classList.remove('active');
            
            if (sectionBody && sectionBody.style.display !== 'none') {
                sectionBody.style.display = 'none';
                
                // Update ARIA attributes
                if (sectionHeader) {
                    sectionHeader.setAttribute('aria-expanded', 'false');
                }
                
                if (section.classList.contains('completed') && editLink) {
                    editLink.innerHTML = '<i class="bi bi-pencil me-1" aria-hidden="true"></i>Edit';
                    editLink.style.display = 'inline';
                }
            }
        });
    }

    /**
     * Toggle billing address form
     */
    toggleBilling(event) {
        const isChecked = event.target.checked;
        const billingFormDiv = document.getElementById('billing-address-form');
        
        if (billingFormDiv) {
            billingFormDiv.style.display = isChecked ? 'none' : 'block';
        }
    }

    /**
     * Set button to loading state
     */
    setButtonLoading(button, loadingText = 'Saving...') {
        button.disabled = true;
        button.dataset.originalText = button.innerHTML;
        button.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${loadingText}`;
    }

    /**
     * Reset button from loading state
     */
    resetButton(button) {
        button.disabled = false;
        if (button.dataset.originalText) {
            button.innerHTML = button.dataset.originalText;
            delete button.dataset.originalText;
        }
    }

    /**
     * Scroll to payment error if card was declined
     */
    scrollToPaymentError() {
        setTimeout(() => {
            const errorAlert = document.querySelector('.alert-danger');
            if (errorAlert) {
                errorAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorAlert.classList.add('shadow-lg');
            }
        }, 500);
    }
}
